import MomentUtils from '@date-io/moment';
import { Box, Button, CircularProgress, Tooltip } from '@material-ui/core';
import { ButtonProps } from '@material-ui/core/Button';
import { pink } from '@material-ui/core/colors';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import React, { Fragment, useState } from 'react';
import useConfirmDialog, { IDialogConfig } from '../../hooks/useConfirmDialog';

interface IProps {
  mode: 'create' | 'view' | 'edit';
  pending?: boolean;
  submitTitle?: string;
  editTitle?: string;
  disableModal?: boolean;
  onEditClick?: () => void;
  onSubmitClick: () => void;
  onCancelClick?: () => void;
  onViewCancelClick?: () => void;
  onDeleteClick?: () => void;
  validationMessages?: string[];
  fullWidth?: boolean;
  prepend?: JSX.Element;
  hideIcon?: boolean;
  ConfirmDialogProps?: Partial<IDialogConfig>;
}

const useStyles = makeStyles((theme: Theme) => ({
  editButton: {
    fontSize: 14,
    fontWeight: 500,
    width: 'auto',
    minWidth: '80px',
    marginRight: theme.spacing(1),
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  submitButtonProgress: {
    color: pink[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  filterQuery: { padding: '0 20px 6px' },
}));

export const SubmitControl = (props: IProps & ButtonProps): JSX.Element => {
  const classes = useStyles();
  const {
    mode,
    pending,
    onEditClick,
    onSubmitClick,
    onViewCancelClick,
    onCancelClick,
    onDeleteClick,
    validationMessages,
    submitTitle,
    editTitle = 'EDIT',
    disableModal,
    className,
    hideIcon,
    ConfirmDialogProps,
  } = props;

  const confirm = useConfirmDialog();
  const isValid = !validationMessages || validationMessages.length === 0;

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      width={props.fullWidth ? '100%' : 'auto'}
      justifyContent="flex-end"
      style={{ flex: 1 }}
      className={className}
    >
      {props.prepend || null}
      {mode === 'view' && onViewCancelClick && (
        <Button variant="text" color="default" className={classes.cancelButton} onClick={onViewCancelClick}>
          CANCEL
        </Button>
      )}
      {mode !== 'view' && onCancelClick && (
        <Button variant="text" color="default" className={classes.cancelButton} onClick={onCancelClick}>
          CANCEL
        </Button>
      )}
      {mode !== 'view' && (
        <Tooltip
          title={
            <div>
              {validationMessages?.map((msg, i) => (
                <div key={`validation-msg-${msg}-${i}`}>{msg}</div>
              ))}
            </div>
          }
          enterDelay={500}
          leaveDelay={200}
          disableFocusListener={isValid}
          disableHoverListener={isValid}
          arrow
        >
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (!disableModal) {
                  confirm.open({
                    title: 'Are you sure?',
                    text: 'This will affect the actual database.',
                    ...ConfirmDialogProps,
                    onProceed: onSubmitClick,
                  });
                } else {
                  onSubmitClick();
                }
              }}
              disabled={pending || (validationMessages && validationMessages.length > 0)}
              style={{
                textTransform: 'capitalize',
              }}
            >
              {submitTitle ? submitTitle : mode === 'edit' ? 'UPDATE' : 'SUBMIT'}
              {pending && <CircularProgress size={24} className={classes.submitButtonProgress} />}
            </Button>
          </div>
        </Tooltip>
      )}

      {mode === 'view' && onDeleteClick && (
        <Button onClick={onDeleteClick} variant="text" color="default" className={classes.editButton}>
          <Fragment>
            DELETE
            {!hideIcon && (
              <DeleteIcon
                style={{
                  paddingLeft: 4,
                }}
              />
            )}
          </Fragment>
        </Button>
      )}

      {mode === 'view' && (
        <Button onClick={onEditClick} variant="contained" color="primary" className={classes.editButton}>
          <Fragment>
            {editTitle}
            {!hideIcon && (
              <EditIcon
                style={{
                  paddingLeft: 4,
                }}
              />
            )}
          </Fragment>
        </Button>
      )}
    </Box>
  );
};

interface IDateProps {
  label: string;
  readonly?: boolean;
  dateValue: Date | null;
  onChange: (value: Date) => void;
}

export const DateComponent = (props: IDateProps) => {
  const readonly = props.readonly || false;
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <KeyboardDateTimePicker
        label={props.label}
        format="lll"
        margin="normal"
        id="date-picker-inline"
        value={props.dateValue}
        onChange={(date, value) => {
          props.onChange(date!.toDate());
        }}
        onClick={() => {
          if (!readonly) {
            setShowCalendar(true);
          }
        }}
        onClose={() => {
          setShowCalendar(false);
        }}
        open={showCalendar}
        disabled={readonly}
        InputLabelProps={{ shrink: true }}
        inputProps={{ readOnly: true }}
      />
    </MuiPickersUtilsProvider>
  );
};
